// Trivia Search Bar Component
// Search bar for finding and adding trivia questions to featured content

import React, { useState, useEffect } from 'react';
import { logger } from '../../../core';
import './TriviaSearchBar.css';

export function TriviaSearchBar({ onAddTrivia }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);

  // Debounced search
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      performSearch(searchQuery);
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  const performSearch = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    setIsSearching(true);
    try {
      const response = await fetch(`/api/learn/trivia?search=${encodeURIComponent(query.trim())}&limit=10`);
      const data = await response.json();

      if (data.success && data.trivia) {
        setSearchResults(data.trivia);
        setShowResults(true);
      } else {
        setSearchResults([]);
        setShowResults(false);
      }
    } catch (error) {
      logger.error({
        tags: 'admin|featured|trivia-search|error',
        color1: 'red',
      }, 'Trivia Search error:', error);
      setSearchResults([]);
      setShowResults(false);
    } finally {
      setIsSearching(false);
    }
  };

  const handleAddTrivia = (trivia) => {
    onAddTrivia(trivia);
    setSearchQuery('');
    setShowResults(false);

    logger.log({
      tags: 'admin|featured|trivia-add',
      color1: 'green',
    }, `Added trivia to featured content: ${trivia.question}`);
  };

  const handleSearchFocus = () => {
    if (searchResults.length > 0) {
      setShowResults(true);
    }
  };

  const handleSearchBlur = () => {
    // Delay hiding results to allow clicking
    setTimeout(() => setShowResults(false), 200);
  };

  return (
    <div className="trivia-search-bar">
      <div className="search-container">
        <input
          type="text"
          className="search-input"
          placeholder="Search for trivia to feature..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onFocus={handleSearchFocus}
          onBlur={handleSearchBlur}
        />

        {isSearching && (
          <div className="search-loading">
            <i className="fa-solid fa-spinner fa-spin"></i>
          </div>
        )}
      </div>

      {/* Search results dropdown */}
      {showResults && searchResults.length > 0 && (
        <div className="search-results-dropdown">
          {searchResults.map((trivia) => (
            <button
              key={trivia.id}
              className="search-result-item"
              onClick={() => handleAddTrivia(trivia)}
            >
              <div className="result-info">
                <span className="result-title">{trivia.question}</span>
                <span className="result-description">
                  {trivia.questionType} • {trivia.difficulty}
                </span>
              </div>
              <i className="fa-solid fa-plus add-icon"></i>
            </button>
          ))}
        </div>
      )}

      {showResults && searchResults.length === 0 && searchQuery.trim() && !isSearching && (
        <div className="search-no-results">
          <i className="fa-solid fa-search"></i>
          <p>No trivia found for "{searchQuery}"</p>
          <small>Try different keywords</small>
        </div>
      )}
    </div>
  );
}